/**
 * THE Sub-Goal → rule id derivation (TV1-2377).
 *
 * ── Why this lives in the DOMAIN package and not in the server module ────────
 *
 * The id is a SHARED SECRET between two sides that never talk:
 *
 *  - the SERVER mints it at approval (`subGoalRuleAssignation.policy.ts` issues
 *    `SetRule` with it), re-derives it at promotion to unshadow the right rule
 *    (`subGoalPromotionUnshadow.policy.ts`) and stamps it onto the readmodel row
 *    at `SubGoalPromotionRecorded` (`automationRequests.aggregate.ts`);
 *  - the WEB APP needs it BEFORE promotion, to show the seller the shadow
 *    evidence she is being asked to decide on. `AutomationRequestsReadmodelType
 *    .ruleId` is written only by `SubGoalPromotionRecorded`, so for an
 *    `approved`-but-not-yet-promoted Sub-Goal — exactly the state in which the
 *    decision is made — the row carries no handle on the rule at all.
 *
 * If the two sides ever disagree about this id, NOTHING fails loudly: promotion
 * unshadows a rule that does not exist, and the evidence panel shows the
 * "no rule is watching" treatment for a rule that is watching. Both are green,
 * both are silent, and both are wrong in the reassuring direction. One
 * definition, imported by both, is the only property that prevents it — hence
 * this module, and hence the byte-pinning test beside it.
 *
 * ── Why the hash is spelled out here instead of `require('uuidv5')` ──────────
 *
 * The `uuidv5` npm package is Node-only: `uuid.js` does `require('crypto')` and
 * builds `Buffer`s. It cannot run in the browser bundle at all, and it is not an
 * ESM module either (`main: uuid.js`, no `exports`/`module`), which is the exact
 * shape that has already broken this app's Vite dev server once (see the
 * `risonEsmInterop` plugin in `web-app/vite.config.ts`). So the FE-safe channel
 * cannot be a re-export of that package.
 *
 * The 40 lines below are therefore a from-scratch, dependency-free SHA-1 and the
 * RFC-4122 v5 layout — and they are held byte-identical to `uuidv5('oid', …)` by
 * `subGoalRuleId.test.ts`, which runs BOTH implementations over the same inputs
 * and additionally pins a hardcoded `(subGoalId → ruleId)` pair. Editing a seed
 * or the hash therefore fails loudly, which is the whole point.
 *
 * This module is deliberately free of `@bett3r-dev/jsonschema-definer` imports:
 * it is reachable from the web app through the jsonschema-free
 * `@bett3r-dev/teselly-domain/automation/constants` subpath (re-exported by
 * `constants.ts`). See the `frontend_domain_values_via_constants_subpath` rule.
 */

/**
 * The uuidv5 seed namespaces. Two DIFFERENT seeds off the one Sub-Goal id, because a
 * definition and a rule instance are different objects and colliding their ids would make a
 * `SetRule` and a `DefineRuleDefinition` address the same stream name in two aggregates —
 * legal, and impossible to read.
 *
 * ⚠️ THESE TWO STRINGS ARE PERSISTED IDENTITY. Changing either one renames every
 * rule ever derived, orphaning the live ones. They are pinned by test.
 */
const RULE_DEFINITION_SEED = 'automation-sub-goal-rule-definition';
const RULE_SEED = 'automation-sub-goal-rule';

/** RFC 4122 namespace OID — the `'oid'` namespace of the `uuidv5` package. */
const NAMESPACE_OID = [
  0x6b, 0xa7, 0xb8, 0x12, 0x9d, 0xad, 0x11, 0xd1,
  0x80, 0xb4, 0x00, 0xc0, 0x4f, 0xd4, 0x30, 0xc8
];

const rotateLeft = ( value: number, bits: number ): number =>
  (( value << bits ) | ( value >>> ( 32 - bits ))) >>> 0;

/** SHA-1 over a byte array, returning 20 bytes. No dependencies, no `Buffer`. */
const sha1 = ( bytes: number[]): number[] => {
  const message = bytes.slice();
  const bitLength = message.length * 8;

  message.push( 0x80 );
  while ( message.length % 64 !== 56 ) message.push( 0 );
  // 64-bit big-endian length. Inputs here are short seeds, so the high word is
  // computed rather than assumed zero only for correctness of the general case.
  const high = Math.floor( bitLength / 0x100000000 );
  const low = bitLength >>> 0;
  for ( let i = 3; i >= 0; i-- ) message.push(( high >>> ( i * 8 )) & 0xff );
  for ( let i = 3; i >= 0; i-- ) message.push(( low >>> ( i * 8 )) & 0xff );

  let [ h0, h1, h2, h3, h4 ] = [ 0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0 ];

  for ( let chunk = 0; chunk < message.length; chunk += 64 ) {
    const w = new Array<number>( 80 );
    for ( let i = 0; i < 16; i++ )
      w[i] = (
        ( message[chunk + i * 4] << 24 )
        | ( message[chunk + i * 4 + 1] << 16 )
        | ( message[chunk + i * 4 + 2] << 8 )
        | message[chunk + i * 4 + 3]
      ) >>> 0;
    for ( let i = 16; i < 80; i++ )
      w[i] = rotateLeft( w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1 );

    let [ a, b, c, d, e ] = [ h0, h1, h2, h3, h4 ];
    for ( let i = 0; i < 80; i++ ) {
      let f: number;
      let k: number;
      if ( i < 20 ) { f = ( b & c ) | ( ~b & d ); k = 0x5a827999; } else if ( i < 40 ) { f = b ^ c ^ d; k = 0x6ed9eba1; } else if ( i < 60 ) { f = ( b & c ) | ( b & d ) | ( c & d ); k = 0x8f1bbcdc; } else { f = b ^ c ^ d; k = 0xca62c1d6; }
      const temp = ( rotateLeft( a, 5 ) + ( f >>> 0 ) + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = rotateLeft( b, 30 );
      b = a;
      a = temp;
    }

    h0 = ( h0 + a ) >>> 0;
    h1 = ( h1 + b ) >>> 0;
    h2 = ( h2 + c ) >>> 0;
    h3 = ( h3 + d ) >>> 0;
    h4 = ( h4 + e ) >>> 0;
  }

  const digest: number[] = [];
  for ( const h of [ h0, h1, h2, h3, h4 ])
    for ( let i = 3; i >= 0; i-- ) digest.push(( h >>> ( i * 8 )) & 0xff );
  return digest;
};

/**
 * UTF-8 bytes of a string, without `Buffer` or `TextEncoder` availability assumptions.
 *
 * ⚠️ Known divergence from the reference (`uuidv5`/Node `Buffer`), swept exhaustively
 * and found to be the ONLY one: an UNPAIRED surrogate. `for...of` iterates by code
 * point and yields the lone surrogate as-is (`code` in `0xd800`-`0xdfff`), which this
 * function then encodes CESU-8-style as a 3-byte sequence; Node's `Buffer` instead
 * replaces an unpaired surrogate with U+FFFD before encoding. The two implementations
 * would derive DIFFERENT ids for such an input. Unreachable in practice — sub-goal ids
 * are server-minted UUIDs, never seller-authored text — but a future caller passing
 * user-supplied text through this function should know the boundary exists.
 */
const utf8Bytes = ( value: string ): number[] => {
  const bytes: number[] = [];
  for ( const char of value ) {
    const code = char.codePointAt( 0 ) as number;
    if ( code < 0x80 ) bytes.push( code );
    else if ( code < 0x800 ) bytes.push( 0xc0 | ( code >> 6 ), 0x80 | ( code & 0x3f ));
    else if ( code < 0x10000 )
      bytes.push( 0xe0 | ( code >> 12 ), 0x80 | (( code >> 6 ) & 0x3f ), 0x80 | ( code & 0x3f ));
    else {
      bytes.push(
        0xf0 | ( code >> 18 ),
        0x80 | (( code >> 12 ) & 0x3f ),
        0x80 | (( code >> 6 ) & 0x3f ),
        0x80 | ( code & 0x3f )
      );
    }
  }
  return bytes;
};

const hex = ( byte: number ): string => byte.toString( 16 ).padStart( 2, '0' );

/**
 * `uuidv5('oid', name)`, exactly — same namespace bytes, same SHA-1, same
 * version/variant patching, same lowercase 8-4-4-4-12 rendering.
 */
const uuidV5Oid = ( name: string ): string => {
  const digest = sha1( NAMESPACE_OID.concat( utf8Bytes( name )));
  const uuid = digest.slice( 0, 16 );
  uuid[6] = ( uuid[6] & 0x0f ) | 0x50; // version 5
  uuid[8] = ( uuid[8] & 0x3f ) | 0x80; // RFC 4122 variant
  const raw = uuid.map( hex ).join( '' );
  return [
    raw.slice( 0, 8 ), raw.slice( 8, 12 ), raw.slice( 12, 16 ), raw.slice( 16, 20 ), raw.slice( 20 )
  ].join( '-' );
};

/**
 * DETERMINISTIC, derived from the Sub-Goal id.
 *
 * `SetRule`'s handler is `data.ruleId || randomUUID()`, so a redelivered command WITHOUT an
 * id mints a SECOND rule bound to the same entity — and duplicated rules BOTH FIRE, which on
 * a promoted rule is a silent double-dispatch of real commands. The outbox delivers
 * at-least-once, so this is not a hypothetical.
 *
 * `DefineRuleDefinition` has the same property for the same reason: the definition stream id
 * must be re-derivable by the sibling policy, which never sees the other's events.
 */
export const ruleDefinitionIdForSubGoal = ( subGoalId: string ): string =>
  uuidV5Oid( `${ RULE_DEFINITION_SEED }-${ subGoalId }` );

export const ruleIdForSubGoal = ( subGoalId: string ): string =>
  uuidV5Oid( `${ RULE_SEED }-${ subGoalId }` );
